import { ChangeDetectionStrategy, Component, computed, signal, ViewChild } from '@angular/core';
import { WindowService } from '@progress/kendo-angular-dialog';
import { FlowManagementService } from "../services/flow-management.service";
import { Flow } from "../obj/Flow";
import { mapWithConcurrency } from "../services/concurrency.util";
import { ChildFlowsReplaceConnectionComponent } from './flows.child-flows-replace-connection.component';

interface CallerRef {
  environment: string;
  flowName: string;
  displayName: string;
  portalUrl: string;
  actionName: string;
}

export interface ConnRefInfo {
  name: string;
  api: string;
  mode: string;
  displayName?: string;
}

export interface ChildFlowRow {
  flow: Flow;
  environment: string;
  displayName: string;
  flowName: string;
  resolved: boolean;
  flowState: string;
  triggerKind: string;
  portalUrl: string;
  runMode: 'Invoker' | 'Service Account' | 'Mixed' | 'Unknown';
  runModeDetail: string;
  connectionRefs: ConnRefInfo[];
  /** Comma-separated unique connector api names — backs the Connections column filter. */
  connectorsText: string;
  callers: CallerRef[];   // always [] now — caller-resolution dropped for perf
  callerCount: number;
  callersText: string;
  lastRun: Date | null;
}

const LOG = '[ChildFlowsReport]';

@Component({
  selector: 'app-flows-child-flows',
  templateUrl: './flows.child-flows.component.html',
  styleUrl: './flows.child-flows.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false
})
export class FlowsChildFlowsComponent {
  apiRoot: string = "https://flow.microsoft.com";

  constructor(
    private flowManagementService: FlowManagementService,
    private windowService: WindowService,
  ) {}

  loading = signal(false);
  status = signal<string>('');
  environments = signal<any[]>([]);
  environment_current = signal<number>(0);
  flows = signal<Flow[]>([]);
  candidates_total = signal<number>(0);
  candidates_done = signal<number>(0);
  runs_total = signal<number>(0);
  runs_done = signal<number>(0);
  child_rows = signal<ChildFlowRow[]>([]);

  search = signal<string>('');
  selection: string[] = [];

  view = computed<ChildFlowRow[]>(() => {
    const q = this.search().trim().toLowerCase();
    const rows = this.child_rows();
    if (!q) return rows;
    return rows.filter(r => {
      const hay = [
        r.environment, r.displayName, r.flowName, r.flowState, r.triggerKind,
        r.runMode, r.runModeDetail,
        ...(r.connectionRefs || []).map(c => `${c.api} ${c.mode}`),
      ].join(' ').toLowerCase();
      return hay.includes(q);
    });
  });

  /** Distinct flow states across the current rows — feeds the State multicheck. */
  stateValues = computed<string[]>(() =>
    Array.from(new Set(this.child_rows().map(r => r.flowState).filter(Boolean))).sort());

  /** Fixed enum for Run As — order is intentional, most-interesting first. */
  runModeValues: string[] = ['Invoker', 'Mixed', 'Service Account', 'Unknown'];

  /** Distinct connector api names across rows — feeds the Connections multicheck. */
  connectorValues = computed<string[]>(() => {
    const set = new Set<string>();
    for (const r of this.child_rows()) {
      for (const c of (r.connectionRefs || [])) {
        if (c.api) set.add(c.api);
      }
    }
    return Array.from(set).sort();
  });

  selectedInvokerCount(): number {
    const keys = new Set(this.selection);
    return this.child_rows().filter(r => keys.has(r.flowName) && r.connectionRefs.some(c => c.mode === 'Invoker')).length;
  }

  @ViewChild('grid', { static: true }) grid;

  private classifyRunMode(connectionReferences: any): {
    mode: ChildFlowRow['runMode'];
    detail: string;
    refs: ConnRefInfo[];
  } {
    const refs: ConnRefInfo[] = [];
    if (!connectionReferences || typeof connectionReferences !== 'object') {
      return { mode: 'Unknown', detail: 'no connection references', refs };
    }
    let invoker = 0;
    let embedded = 0;
    let other = 0;
    for (const [name, ref] of Object.entries<any>(connectionReferences)) {
      const apiName: string = ref?.api?.name || ref?.id?.split('/').pop() || '';
      let mode: string;
      if (ref?.source === 'Invoker') {
        mode = 'Invoker';
        invoker++;
      } else if (ref?.runtimeSource === 'embedded' || ref?.source === 'Embedded') {
        mode = 'Embedded';
        embedded++;
      } else {
        mode = 'Owner';
        other++;
      }
      refs.push({ name, api: apiName, mode });
    }
    const total = invoker + embedded + other;
    if (total === 0) return { mode: 'Unknown', detail: 'no connections', refs };
    if (invoker > 0 && embedded === 0 && other === 0) return { mode: 'Invoker', detail: `${invoker} invoker`, refs };
    if (invoker === 0) return { mode: 'Service Account', detail: `${embedded + other} owner`, refs };
    return { mode: 'Mixed', detail: `${invoker} invoker, ${embedded + other} owner`, refs };
  }

  /**
   * Trigger filter — uses the trigger summary already on the Flow object (set
   * by getFlows during the listing parse). No `getFlow` call needed here.
   * A child-flow-capable trigger is `type:Request, kind:Button`.
   */
  private isChildFlowCandidate(flow: Flow): boolean {
    const triggers = flow.triggers || [];
    return triggers.some((t: any) => t?.type === 'Request' && t?.kind === 'Button');
  }

  private async getConnectionReferences(flow: Flow): Promise<any | null> {
    const preferAdmin = (flow.sources || []).includes('admin') || !!flow.solution;
    const order = preferAdmin ? [true, false] : [false, true];
    for (const admin of order) {
      try {
        const f: any = await this.flowManagementService.getFlow(flow.environment, flow.flowName, admin).toPromise();
        if (f) return f?.properties?.connectionReferences || null;
      } catch (ex: any) {
        console.debug(`${LOG} getFlow(${flow.flowName}, admin=${admin}) failed`, ex?.status || ex?.message || ex);
      }
    }
    return null;
  }

  async load() {
    const t0 = performance.now();
    console.log(`${LOG} load() start`);

    this.loading.set(true);
    this.status.set('Loading current environment…');
    this.child_rows.set([]);
    this.selection = [];
    this.environment_current.set(0);
    this.candidates_total.set(0);
    this.candidates_done.set(0);
    this.runs_total.set(0);
    this.runs_done.set(0);

    try {
      const envName = this.flowManagementService.currentEnvironment;
      if (!envName) {
        console.warn(`${LOG} no current environment selected`);
        this.status.set('No current environment selected. Pick one from the Flows list first.');
        return;
      }
      console.log(`${LOG} environment: ${envName}`);

      let env: any = null;
      try {
        env = await this.flowManagementService.getEnvironment(envName).toPromise();
      } catch (ex) {
        console.warn(`${LOG} getEnvironment failed`, ex);
      }
      this.environments.set(env ? [env] : [{ name: envName }]);
      this.environment_current.set(1);

      // Phase 1: load all flows in env (one listing call). Owner + shared +
      // solution. No getRuns / no getTrigger — those would add latency.
      this.status.set('Loading flows…');
      const tLoad = performance.now();
      let all: Flow[] = [];
      try {
        all = await this.flowManagementService.getFlows(envName, false, false, false, false, true);
      } catch (ex) {
        console.error(`${LOG} getFlows failed`, ex);
        this.status.set(`Error loading flows: ${(ex as any)?.message || ex}`);
        return;
      }
      this.flows.set(all);
      console.log(`${LOG} loaded ${all.length} flows in ${Math.round(performance.now() - tLoad)}ms`);

      // Phase 2: filter by trigger summary. No getFlow needed — `flow.triggers`
      // was populated by resolveTriggers() during the listing parse.
      const candidates = all.filter((f) => this.isChildFlowCandidate(f));
      this.candidates_total.set(candidates.length);
      console.log(`${LOG} ${candidates.length} candidates (Request+Button trigger) out of ${all.length} flows`);

      if (candidates.length === 0) {
        this.status.set(`No manual-triggered flows found (scanned ${all.length}).`);
        return;
      }

      // Build initial rows up front so the table populates immediately. Run
      // mode starts as Unknown and gets upgraded as connectionReferences
      // resolve in phase 3.
      const rows: ChildFlowRow[] = candidates.map((child) => ({
        flow: child,
        environment: child.environment,
        displayName: child.displayName,
        flowName: child.flowName,
        resolved: true,
        flowState: child.flowState,
        triggerKind: child.triggerKind,
        portalUrl: this.apiRoot + child.portalUrl,
        runMode: 'Unknown' as const,
        runModeDetail: 'loading…',
        connectionRefs: [],
        connectorsText: '',
        callers: [],
        callerCount: 0,
        callersText: '',
        lastRun: null,
      }));
      this.child_rows.set(rows);

      const rowByFlowName = new Map<string, ChildFlowRow>();
      for (const r of rows) rowByFlowName.set(r.flowName, r);

      // Phase 3: getFlow per candidate to extract connectionReferences.
      // Concurrency 3 — keeps a couple of large responses in flight without
      // blowing memory. Each iteration owns its connectionReferences and lets
      // the rest of the payload go out of scope immediately.
      this.status.set(`Reading connections for ${candidates.length} candidate(s)…`);
      const tConn = performance.now();
      await mapWithConcurrency(candidates, async (flow) => {
        const connRefs = await this.getConnectionReferences(flow);
        const row = rowByFlowName.get(flow.flowName);
        if (!row) return;
        const { mode, detail, refs } = this.classifyRunMode(connRefs);
        row.runMode = mode;
        row.runModeDetail = detail;
        row.connectionRefs = refs;
        row.connectorsText = Array.from(new Set(refs.map(r => r.api).filter(Boolean))).join(', ');
      }, {
        concurrency: 3,
        onProgress: (done, total) => {
          this.candidates_done.set(done);
          if (done % 5 === 0 || done === total) {
            this.status.set(`Reading connections (${done}/${total})…`);
          }
        },
      });
      console.log(`${LOG} connection-refs pass done in ${Math.round(performance.now() - tConn)}ms`);

      // Sort: Invoker → Mixed → Service Account → Unknown.
      const order: Record<ChildFlowRow['runMode'], number> = {
        'Invoker': 0, 'Mixed': 1, 'Service Account': 2, 'Unknown': 3,
      };
      rows.sort((a, b) => (order[a.runMode] - order[b.runMode]) ||
        (a.environment || '').localeCompare(b.environment || '') ||
        (a.displayName || '').localeCompare(b.displayName || ''));

      this.child_rows.set([...rows]);
      const invokerCount = rows.filter(r => r.runMode === 'Invoker').length;
      const mixedCount = rows.filter(r => r.runMode === 'Mixed').length;
      const saCount = rows.filter(r => r.runMode === 'Service Account').length;
      const unkCount = rows.filter(r => r.runMode === 'Unknown').length;
      console.log(`${LOG} classified: ${invokerCount} invoker, ${mixedCount} mixed, ${saCount} service-account, ${unkCount} unknown`);

      // Phase 4: live-API last run per row. Best-effort; getFlowRuns already
      // does a 403→admin retry, so user-scope flows whose runs are gated to
      // admin still resolve. Push the table once after the pass completes.
      this.status.set(`Resolving last run for ${rows.length} flow(s)…`);
      this.runs_total.set(rows.length);
      const tRuns = performance.now();
      let runsResolved = 0;
      await mapWithConcurrency(rows, async (row) => {
        if (!row.flow) return;
        try {
          const runs = await this.flowManagementService.getFlowRuns(row.flow, false, false, false, 0, true);
          if (runs && runs.length > 0 && runs[0].startTime) {
            row.lastRun = runs[0].startTime;
            runsResolved++;
          }
        } catch (ex) {
          // silent
        }
      }, {
        concurrency: 3,
        onProgress: (done, total) => {
          this.runs_done.set(done);
          if (done % 10 === 0 || done === total) {
            this.status.set(`Resolving last runs (${done}/${total})…`);
          }
        },
      });
      console.log(`${LOG} last-run pass done in ${Math.round(performance.now() - tRuns)}ms — ${runsResolved} of ${rows.length} have a run`);

      this.child_rows.set([...rows]);
      this.status.set(`Done — ${rows.length} child-flow candidate${rows.length === 1 ? '' : 's'} from ${all.length} flows.`);
      console.log(`${LOG} load() complete in ${Math.round(performance.now() - t0)}ms`);
    } catch (ex) {
      console.error(`${LOG} load() failed`, ex);
      this.status.set(`Error: ${(ex as any)?.message || ex}`);
    } finally {
      this.loading.set(false);
    }
  }

  downloadCsv() {
    this.grid.saveAsExcel();
  }

  canReplaceConnections(): boolean {
    return this.selectedInvokerCount() > 0;
  }

  async openReplaceConnections() {
    const keys = new Set(this.selection);
    const rows = this.child_rows().filter(r => keys.has(r.flowName) && r.resolved && r.connectionRefs.some(c => c.mode === 'Invoker'));
    if (rows.length === 0) return;

    const dialogRef = this.windowService.open({
      title: `Replace Invoker Connections (${rows.length} flow${rows.length === 1 ? '' : 's'})`,
      content: ChildFlowsReplaceConnectionComponent,
      keepContent: false,
      minHeight: 420,
      minWidth: 640,
      width: 720,
      top: 40,
    });
    const inst: ChildFlowsReplaceConnectionComponent = dialogRef.content.instance;
    inst.rows = rows;
    inst.environment = rows[0]?.environment || this.flowManagementService.currentEnvironment;
    inst.applied.subscribe((updated: string[]) => {
      if (updated?.length) {
        this.status.set(`Replaced connections on ${updated.length} flow${updated.length === 1 ? '' : 's'}.`);
      }
    });
  }
}
